import { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'wouter';
import { Check, ChevronRight } from 'lucide-react';
import type { QuizQuestion } from '@shared/types';
import { getEventType } from '@shared/eventTypes';
import { useAllQuizQuestions } from '@/data/quiz';
import { useProfile, useUpdateProfile, useInvalidateProfile } from '@/lib/api';
import { QuizFieldSelect } from '@/components/ux/QuizFieldSelect';
import { EventTypeSelectModal } from '@/components/EventTypeSelectModal';
import { PageHeader } from '@/components/ux/PageHeader';
import { Pill } from '@/components/ux/Pill';
import { useT } from '@/lib/i18n';
import { Loader } from '@/components/Loader';

// Identity, contact & basic fields are edited inline on the profile itself (the
// header + Basic info section); everything else (vibe, about-you, values) is the
// "personality" surface shown here — plus the matching preferences (who you want
// to meet, age groups, event interests) folded in from the retired Interests tab.
const EXCLUDED = new Set([
  'location',
  'phone',
  'instagram',
  'birthday',
  'genderIdentity',
  'pronouns',
  'sexualOrientation',
  // "Topics you could chat about forever" is intentionally dropped here.
  // careerStage/industry are KEPT — the matcher reads them for seating; the
  // free-text Work field in Basic info is a separate, display-only thing.
  'conversationTopics',
]);

const isEmpty = (v: any) =>
  v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && [...a].sort().join('|') === [...b].sort().join('|');

/** Array-aware equality for answer comparison. */
const valuesEqual = (a: any, b: any): boolean => {
  if (isEmpty(a) && isEmpty(b)) return true;
  if (Array.isArray(a) || Array.isArray(b)) {
    return sameSet(Array.isArray(a) ? a : [], Array.isArray(b) ? b : []);
  }
  return a === b;
};

const asArray = (v: unknown): string[] => (Array.isArray(v) ? v : []);

export default function EditPersonality() {
  const { t } = useT();
  const [, navigate] = useLocation();
  const allQuestions = useAllQuizQuestions();
  const questions = useMemo(
    // `onboardingOnly` questions belong to the onboarding flow experiment —
    // they're still resolved for label lookup elsewhere, just not editable here.
    () => allQuestions.filter((q) => !EXCLUDED.has(q.id) && !q.onboardingOnly),
    [allQuestions],
  );

  const { data: profileData, isLoading } = useProfile({ staleTime: 0 });
  const updateProfile = useUpdateProfile();
  const invalidateProfile = useInvalidateProfile();

  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [initialValues, setInitialValues] = useState<Record<string, any>>({});
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState('');

  // Matching preferences — moved here from the old Interests tab. `community`
  // and `ageRangePreferences` live in quizAnswers; `interests` (event-type
  // slugs) is its own top-level field that drives the events feed.
  const [community, setCommunity] = useState('');
  const [ageRangePreferences, setAgeRangePreferences] = useState<string[]>([]);
  const [interests, setInterests] = useState<string[]>([]);
  const [interestsModalOpen, setInterestsModalOpen] = useState(false);
  const [prefsInitial, setPrefsInitial] = useState({
    community: '',
    ageRangePreferences: [] as string[],
    interests: [] as string[],
  });

  // Synthetic quiz questions so these reuse the slide-up QuizFieldSelect UI.
  const communityQuestion = useMemo<QuizQuestion>(
    () => ({
      id: 'community',
      type: 'single',
      required: false,
      question: 'Who you want to meet',
      options: [
        { value: 'men', label: t('quiz.stepper.communityIntro.options.men.label'), emoji: '👬' },
        { value: 'sapphic', label: t('quiz.stepper.communityIntro.options.sapphic.label'), emoji: '👭' },
        { value: 'mixed', label: t('quiz.stepper.communityIntro.options.mixed.label'), emoji: '🌈' },
      ],
    }),
    [t],
  );

  const ageQuestion = useMemo<QuizQuestion>(
    () => ({
      id: 'ageRangePreferences',
      type: 'multiple',
      required: false,
      question: 'Age groups you want to meet',
      options: [
        { value: 'younger', label: t('quiz.stepper.ageIntro.options.younger.label'), emoji: '🌱' },
        { value: 'same', label: t('quiz.stepper.ageIntro.options.same.label'), emoji: '🤝' },
        { value: 'older', label: t('quiz.stepper.ageIntro.options.older.label'), emoji: '🌟' },
      ],
    }),
    [t],
  );

  // Seed answers + matching preferences from the saved profile once it loads.
  useEffect(() => {
    if (initialized || !profileData?.data?.profile) return;
    const p = profileData.data.profile as any;
    const seeded: Record<string, any> = {};
    for (const q of questions) {
      if (!isEmpty(p[q.id])) seeded[q.id] = p[q.id];
    }
    setAnswers(seeded);
    setInitialValues(seeded);

    const prefs = {
      community: p.community || '',
      ageRangePreferences: asArray(p.ageRangePreferences),
      interests: asArray(p.interests),
    };
    setCommunity(prefs.community);
    setAgeRangePreferences(prefs.ageRangePreferences);
    setInterests(prefs.interests);
    setPrefsInitial(prefs);

    setInitialized(true);
  }, [profileData, initialized, questions]);

  const handleAnswer = useCallback((questionId: string, value: any) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  }, []);

  const isAnswered = useCallback((id: string) => !isEmpty(answers[id]), [answers]);
  const isChanged = useCallback(
    (id: string) => !valuesEqual(answers[id], initialValues[id]),
    [answers, initialValues],
  );

  const prefsChanged =
    community !== prefsInitial.community ||
    !sameSet(ageRangePreferences, prefsInitial.ageRangePreferences) ||
    !sameSet(interests, prefsInitial.interests);

  const hasChanges = useMemo(
    () => questions.some((q) => isChanged(q.id)) || prefsChanged,
    [questions, isChanged, prefsChanged],
  );

  // Group by category, preserving definition order.
  const byCategory = useMemo(() => {
    const map = new Map<string, QuizQuestion[]>();
    for (const q of questions) {
      const c = q.category ?? 'Other';
      map.set(c, [...(map.get(c) ?? []), q]);
    }
    return Array.from(map.entries());
  }, [questions]);

  const selectedInterestTypes = interests
    .map((slug) => getEventType(slug))
    .filter((x): x is NonNullable<typeof x> => !!x);

  const handleSave = () => {
    setError('');
    const quizAnswers: Record<string, any> = { ...answers, ageRangePreferences };
    if (community) quizAnswers.community = community;

    updateProfile.mutate(
      { quizAnswers: quizAnswers as any, interests },
      {
        onSuccess: async () => {
          await invalidateProfile();
          navigate('/profile');
        },
        onError: (err) => {
          console.error('Failed to save personality profile:', err);
          setError(t('quiz.errors.saveFailed'));
        },
      },
    );
  };

  if ((isLoading || !initialized) && !updateProfile.isPending) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <Loader />
      </div>
    );
  }

  return (
    <div className="pt-4 pb-24 md:pt-8 px-4">
      <div className="max-w-2xl mx-auto">
        <PageHeader
          title="Connection Profile"
          subtitle="This is how we get to know you. We use it to seat you at dinners, surface the right kikis and events to you, and show your kikis to the people you'll click with. None of it is ever shown on your profile or shared with anyone."
          onBack={() => navigate('/profile')}
          backLabel={t('common.buttons.cancel')}
          action={
            <button
              type="button"
              onClick={handleSave}
              disabled={updateProfile.isPending || !hasChanges}
              className="btn-primary"
            >
              {updateProfile.isPending ? t('common.buttons.loading') : t('common.buttons.save')}
            </button>
          }
        />

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Who you want to meet — matching preferences (folded in from Interests) */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <h3 className="text-lg font-semibold text-dark-text">Who you want to meet</h3>
            <div className="flex-1 h-px bg-dark-border" />
          </div>
          <div className="space-y-4">
            <QuizFieldSelect
              question={communityQuestion}
              value={community}
              onChange={(v) => setCommunity(v)}
            />
            <QuizFieldSelect
              question={ageQuestion}
              value={ageRangePreferences}
              onChange={(v) => setAgeRangePreferences(v)}
            />
            <div className="space-y-1.5">
              <label className="input-label">Events you're into</label>
              <button
                type="button"
                onClick={() => setInterestsModalOpen(true)}
                className="input-glass-wrapper w-full flex items-center justify-between gap-3 text-left hover:border-primary-400 dark:hover:border-primary-500 transition-colors"
              >
                {selectedInterestTypes.length === 0 ? (
                  <span className="text-neutral-400 dark:text-neutral-500 truncate">Select</span>
                ) : (
                  <span className="flex flex-wrap gap-1.5 min-w-0">
                    {selectedInterestTypes.map((tp) => (
                      <Pill
                        key={tp.slug}
                        variant="soft"
                        size="sm"
                        leading={<span>{tp.emoji}</span>}
                      >
                        {tp.label}
                      </Pill>
                    ))}
                  </span>
                )}
                <ChevronRight className="w-4 h-4 text-neutral-400 flex-shrink-0" />
              </button>
            </div>
          </div>
        </div>

        {byCategory.map(([category, qs]) => {
          const answeredCount = qs.filter((q) => isAnswered(q.id)).length;
          return (
            <div key={category} className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <h3 className="text-lg font-semibold text-dark-text">{category}</h3>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-dark-muted">
                    {answeredCount}/{qs.length}
                  </span>
                  {answeredCount === qs.length && <Check className="w-4 h-4 text-green-500" />}
                </div>
                <div className="flex-1 h-px bg-dark-border" />
              </div>

              <div className="space-y-4">
                {qs.map((q) => (
                  <QuizFieldSelect
                    key={q.id}
                    question={q}
                    value={answers[q.id]}
                    onChange={(value) => handleAnswer(q.id, value)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <EventTypeSelectModal
        isOpen={interestsModalOpen}
        onClose={() => setInterestsModalOpen(false)}
        selected={interests}
        onChange={setInterests}
        title="Your interests"
      />
    </div>
  );
}
